import { cssObj } from '@/css/resources/functions.css';
import { breakpoints, transitions } from '@/css/resources/variables.css';
import { outlineA11y } from '@/css/resources/placeholders.css';
import {
	fontStyle,
	fluidBorderRadius,
	fluidBorderWidth,
	fluidPadding,
	fluidValues,
} from '@/css/resources/mixins.css';

export const wrapperStyles = (styles) =>
	cssObj({
		position: 'relative',
		maxWidth: '100%',
		paddingBottom: `calc(
			(${styles?.error?.typography?.lineHeight?.[1]} * 1px) + ${styles?.error?.typography?.margin?.top} + ${styles?.error?.typography?.margin?.bottom}
		)` /* space taken by the error message */,
	});

export const inputSizeStyles = () =>
	cssObj({
		gridColumn: 'span 12',

		'&.supt-inputField--half': {
			[`@media (min-width: ${breakpoints.medium})`]: {
				gridColumn: 'span 6',
			},
		},
	});

export const inputSizeEditStyles = () =>
	cssObj({
		maxWidth: '100%',
		width: 'calc(100% - 32px)',
		margin: '0 16px !important',
		display: 'inline-block',

		'&[data-size="half"]': {
			[`@media (min-width: ${breakpoints.medium})`]: {
				width: `calc(50% - 32px)`,
			},
		},
	});

export const labelStyles = (styles) =>
	cssObj(labelDynamicStyles(styles), optionalStyles(styles), {
		display: 'flex',
		justifyContent: 'space-between',
		flexWrap: 'wrap',
		position: 'absolute',
		width: '100%',
		top: '0',
		left: '0',
		pointerEvents: 'none',
		zIndex: 1,
	});

export const labelDynamicStyles = (styles) =>
	cssObj(
		fluidPadding(styles?.label?.padding),
		fontStyle(
			{
				...styles?.label?.typography,
				textAlign: styles?.label?.textAlignment,
				color: styles?.label?.color,
			},
			false
		)
	);

export const optionalStyles = (styles) =>
	cssObj(optionalDynamicStyles(styles), {
		display: 'flex',
		alignItems: 'center',

		'&[data-optional]:not([data-optional=""])::after': {
			content: 'attr(data-optional)',
			display: 'inline',
			letterSpacing: '0.5px',
			marginLeft: '12px',
		},
	});

export const optionalDynamicStyles = (styles) =>
	cssObj({
		'&[data-optional]::after': fontStyle(
			{
				...styles?.label?.optional?.typography,
				textAlign: styles?.label?.optional?.textAlignment,
				color: styles?.label?.optional?.color,
			},
			false
		),
	});

export const inputStyles = (styles) =>
	cssObj(inputDynamicStyles(styles), {
		outline: '0',
		borderStyle: 'solid',
		appearance: 'none',
		display: 'block',
		width: '100%',
		transition: `all ${transitions.fast} ease-out`,

		'&[disabled]': {
			cursor: 'not-allowed',
		},

		'&:not([disabled]):hover, &:not([disabled]):focus': inputDynamicStyles(
			styles?.states?.hover
		),

		'html:not(.using-mouse) &:focus-within': outlineA11y,
	});

export const inputDynamicStyles = (styles) =>
	cssObj(
		fontStyle(
			{
				...styles?.input?.typography,
				textAlign: styles?.input?.textAlignment,
				color: styles?.input?.color,
			},
			false
		),
		inputPadding(styles),
		fluidBorderWidth(styles?.input?.border?.width),
		fluidBorderRadius(styles?.input?.border?.radius),
		{
			background: styles?.input?.backgroundColor,
			borderColor: styles?.input?.border?.color,

			"&[aria-invalid='true']": {
				borderColor: styles?.error?.color,
			},

			'::placeholder': {
				color: `${styles?.input?.color}66`,
			},
		}
	);

export const inputPadding = (styles) => {
	let inputFinalPadding = {
		...styles?.input?.padding,
	};

	// Padding top of input includes size and padding top of label + input padding
	if (styles?.label?.padding && styles?.label?.typography?.lineHeight) {
		inputFinalPadding.top = [
			styles?.label?.padding?.top?.[0] +
				styles?.label?.typography?.lineHeight?.[0] +
				styles?.input?.padding?.top?.[0],
			styles?.label?.padding?.top?.[1] +
				styles?.label?.typography?.lineHeight?.[1] +
				styles?.input?.padding?.top?.[1],
		];
	}

	return cssObj(fluidPadding(inputFinalPadding));
};

export const errorStyles = (styles) =>
	cssObj(errorDynamicStyles(styles), {
		marginBottom: '0px',
		position: 'absolute',
		left: '0',
		width: '100%',
	});

export const errorDynamicStyles = (styles) =>
	cssObj(
		fontStyle({
			...styles?.error?.typography,
			textAlign: styles?.error?.textAlignment,
			color: styles?.error?.color,
		})
	);

// EDIT
export const optionalEditStyles = (styles) =>
	cssObj(
		optionalDynamicStyles(styles),
		{
			position: 'absolute',
		},
		fluidValues(
			'top',
			styles?.label?.padding?.top?.[0],
			styles?.label?.padding?.top?.[1]
		),
		fluidValues(
			'right',
			styles?.label?.padding?.right?.[0],
			styles?.label?.padding?.right?.[1]
		)
	);

export const labelEditStyles = () =>
	cssObj({ background: 'none !important', pointerEvents: 'initial' });

export const inputEditStyles = (styles) =>
	cssObj({
		color: `${styles?.input?.color}66`,
	});
